import { helpWhy } from '../../help/why';
import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { MaterialConsumptionService } from '../../services/materialConsumption.service';
import { ItemService } from '../../services/item.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { IMaterialConsumption, IMaterialConsumptionCreateRequest } from '../../interfaces/garments/materialConsumption.interface';
import { MaterialType } from '../../interfaces/garments/styleBom.interface';
import { Item } from '../../interfaces/item.interface';
import { Store } from '../../interfaces/store.interface';
import ItemAutocomplete from './ItemAutocomplete';
import StockLotPicker from '../common/StockLotPicker';
import { enumLabel } from '../../utils/garmentsLabels';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

// Mirrors `MaterialConsumptionCreateDTO`'s @Size bounds (backend/.../merch/dataclass/MaterialConsumptionCreateDTO.java).
const MAX_UOM = 30;
const MAX_REFERENCE = 255;

const MATERIAL_TYPES: MaterialType[] = ['FABRIC', 'YARN', 'TRIMS', 'ACCESSORIES', 'PACKING', 'OTHER'];

interface DraftEntry {
    item: Item | null;
    materialType: MaterialType | '';
    consumedQty: string;
    uom: string;
    consumptionDate: string;
    producedQty: string;
    reference: string;
    remarks: string;
    /** Store picked locally just to feed the StockLotPicker's available-lot lookup — not sent to the backend. */
    storeId: string;
    stockLotId: string | null;
}

const emptyDraft: DraftEntry = {
    item: null, materialType: '', consumedQty: '', uom: '',
    consumptionDate: toLocalIsoDate(), producedQty: '', reference: '', remarks: '',
    storeId: '', stockLotId: null,
};

const draftFromEntry = (entry: IMaterialConsumption, itemLookup: Record<string, Item>): DraftEntry => ({
    item: itemLookup[entry.itemId] ?? null,
    materialType: entry.materialType ?? '',
    consumedQty: String(entry.consumedQty),
    uom: entry.uom,
    consumptionDate: entry.consumptionDate,
    producedQty: entry.producedQty != null ? String(entry.producedQty) : '',
    reference: entry.reference ?? '',
    remarks: entry.remarks ?? '',
    storeId: '',
    stockLotId: entry.stockLotId ?? null,
});

interface MaterialConsumptionEditorProps {
    buyerOrderId: string;
    canManage: boolean;
}

/**
 * Material consumption entry keyed to a buyer order (garments-merchandising T7, gap row 11.5).
 * Each entry has its own create/update/delete endpoint (`MaterialConsumptionService`); rows are
 * added/edited/deleted one at a time against the server and the table reloads after every
 * mutation. Mirrors `StyleBomEditor`'s shape.
 */
export function MaterialConsumptionEditor({ buyerOrderId, canManage }: MaterialConsumptionEditorProps) {
    const toast = useToast();

    const [entries, setEntries] = useState<IMaterialConsumption[]>([]);
    const [itemLookup, setItemLookup] = useState<Record<string, Item>>({});
    const [stores, setStores] = useState<Store[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [adding, setAdding] = useState(false);
    const [addDraft, setAddDraft] = useState<DraftEntry>(emptyDraft);

    const [editingId, setEditingId] = useState<string | null>(null);
    const [editDraft, setEditDraft] = useState<DraftEntry>(emptyDraft);

    const loadItemLookup = useCallback(async () => {
        try {
            const res = await ItemService.getItemsByInstitution(0, 500, '', 'STOCK'); // FW-2A
            setItemLookup(Object.fromEntries((res.content || []).map(i => [i.id as string, i])));
        } catch (e) {
            // Non-fatal — the table falls back to "Item unavailable" when a lookup is missing.
        }
    }, []);

    const loadStores = useCallback(async () => {
        try {
            const res = await getStoresByInstitutionId(0, 100);
            setStores(res.content || []);
        } catch (e) {
            // Non-fatal — the lot picker simply stays disabled when no store is chosen.
        }
    }, []);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            const res = await MaterialConsumptionService.list(institutionId, buyerOrderId);
            setEntries(res);
        } catch (e) {
            toast.showApiError(e, 'Failed to load consumption entries');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyerOrderId]);

    useEffect(() => {
        loadItemLookup();
        loadStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyerOrderId]);

    const itemDisplay = (itemId: string) => {
        const item = itemLookup[itemId];
        return item ? `${item.itemName} (${item.itemCode})` : 'Item unavailable';
    };

    const startAdd = () => {
        setAddDraft(emptyDraft);
        setAdding(true);
    };

    const cancelAdd = () => {
        setAdding(false);
        setAddDraft(emptyDraft);
    };

    const addWhy = saving ? null
        : !addDraft.item ? 'Pick the item first.'
        : null;

    const handleAdd = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !addDraft.item) return;
        const qty = parseFloat(addDraft.consumedQty);
        if (Number.isNaN(qty) || qty < 0 || !addDraft.uom.trim() || !addDraft.consumptionDate) return;
        const request: IMaterialConsumptionCreateRequest = {
            institutionId,
            buyerOrderId,
            itemId: addDraft.item.id as string,
            materialType: addDraft.materialType || null,
            consumedQty: qty,
            uom: addDraft.uom.trim(),
            consumptionDate: addDraft.consumptionDate,
            producedQty: addDraft.producedQty.trim() === '' ? null : parseFloat(addDraft.producedQty),
            reference: addDraft.reference.trim() || null,
            remarks: addDraft.remarks.trim() || null,
            stockLotId: addDraft.stockLotId,
        };
        setSaving(true);
        try {
            await MaterialConsumptionService.create(institutionId, request);
            toast.showSuccess('Consumption entry added');
            cancelAdd();
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to add consumption entry');
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (entry: IMaterialConsumption) => {
        setEditingId(entry.id);
        setEditDraft(draftFromEntry(entry, itemLookup));
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditDraft(emptyDraft);
    };

    const handleSaveEdit = async (id: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        const qty = parseFloat(editDraft.consumedQty);
        if (Number.isNaN(qty) || qty < 0 || !editDraft.uom.trim() || !editDraft.consumptionDate) return;
        setSaving(true);
        try {
            await MaterialConsumptionService.update(institutionId, id, {
                materialType: editDraft.materialType || null,
                consumedQty: qty,
                uom: editDraft.uom.trim(),
                consumptionDate: editDraft.consumptionDate,
                producedQty: editDraft.producedQty.trim() === '' ? null : parseFloat(editDraft.producedQty),
                reference: editDraft.reference.trim() || null,
                remarks: editDraft.remarks.trim() || null,
                stockLotId: editDraft.stockLotId,
            });
            toast.showSuccess('Consumption entry updated');
            cancelEdit();
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to update consumption entry');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        if (!window.confirm('Delete this consumption entry?')) return;
        setSaving(true);
        try {
            await MaterialConsumptionService.remove(institutionId, id);
            toast.showSuccess('Consumption entry deleted');
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to delete consumption entry');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">Material Consumption</Typography>
                {canManage && !adding && (
                    <Button size="small" startIcon={<AddIcon />} data-help="merchandising.consumption.add" onClick={startAdd} disabled={saving}>
                        Add Entry
                    </Button>
                )}
            </Box>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small" data-testid="consumption-table" data-help-section="merchandising.consumption.table">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="merchandising.consumption.col.item">Item</TableCell>
                            <TableCell data-help="merchandising.consumption.col.lot">Lot</TableCell>
                            <TableCell data-help="merchandising.consumption.col.materialType">Material Type</TableCell>
                            <TableCell align="right" data-help="merchandising.consumption.col.consumedQty">Consumed Qty</TableCell>
                            <TableCell data-help="merchandising.consumption.col.uom">UoM</TableCell>
                            <TableCell data-help="merchandising.consumption.col.date">Date</TableCell>
                            <TableCell align="right" data-help="merchandising.consumption.col.producedQty">Produced Qty</TableCell>
                            <TableCell data-help="merchandising.consumption.col.reference">Reference</TableCell>
                            <TableCell data-help="merchandising.consumption.col.remarks">Remarks</TableCell>
                            {canManage && <TableCell align="right" data-help="merchandising.consumption.col.actions">Actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={canManage ? 10 : 9} align="center">Loading…</TableCell>
                            </TableRow>
                        )}
                        {!loading && entries.length === 0 && !adding && (
                            <TableRow>
                                <TableCell colSpan={canManage ? 10 : 9} align="center">No consumption entries</TableCell>
                            </TableRow>
                        )}
                        {!loading && entries.map(entry => {
                            const isEditing = editingId === entry.id;
                            return (
                                <TableRow key={entry.id} data-testid={`consumption-row-${entry.id}`}>
                                    <TableCell sx={{ minWidth: 200 }}>{itemDisplay(entry.itemId)}</TableCell>
                                    <TableCell sx={{ minWidth: 220 }}>
                                        {isEditing ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <TextField
                                                    data-help="merchandising.consumption.col.lot" select size="small" fullWidth label="Store" value={editDraft.storeId}
                                                    onChange={e => setEditDraft(prev => ({ ...prev, storeId: e.target.value, stockLotId: null }))}
                                                >
                                                    <MenuItem value="">—</MenuItem>
                                                    {stores.map(s => <MenuItem key={s.id} value={s.id}>{s.storeName}</MenuItem>)}
                                                </TextField>
                                                <StockLotPicker
                                                    storeId={editDraft.storeId || undefined}
                                                    itemId={editDraft.item?.id}
                                                    value={editDraft.stockLotId}
                                                    onChange={lotId => setEditDraft(prev => ({ ...prev, stockLotId: lotId }))}
                                                />
                                            </Box>
                                        ) : (entry.stockLotLabel ?? '—')}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 130 }}>
                                        {isEditing ? (
                                            <TextField
                                                data-help="merchandising.consumption.col.materialType" select size="small" fullWidth value={editDraft.materialType}
                                                onChange={e => setEditDraft(prev => ({ ...prev, materialType: e.target.value as MaterialType }))}
                                            >
                                                <MenuItem value="">—</MenuItem>
                                                {MATERIAL_TYPES.map(t => <MenuItem key={t} value={t}>{enumLabel(t)}</MenuItem>)}
                                            </TextField>
                                        ) : enumLabel(entry.materialType)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ minWidth: 90 }}>
                                        {isEditing ? (
                                            <TextField
                                                data-help="merchandising.consumption.col.consumedQty" size="small" type="number" value={editDraft.consumedQty}
                                                onChange={e => setEditDraft(prev => ({ ...prev, consumedQty: e.target.value }))}
                                            />
                                        ) : entry.consumedQty}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 80 }}>
                                        {isEditing ? (
                                            <TextField
                                                data-help="merchandising.consumption.col.uom" size="small" value={editDraft.uom} inputProps={{ maxLength: MAX_UOM }}
                                                onChange={e => setEditDraft(prev => ({ ...prev, uom: e.target.value }))}
                                            />
                                        ) : entry.uom}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 140 }}>
                                        {isEditing ? (
                                            <TextField
                                                data-help="merchandising.consumption.col.date" size="small" type="date" value={editDraft.consumptionDate}
                                                InputLabelProps={{ shrink: true }}
                                                onChange={e => setEditDraft(prev => ({ ...prev, consumptionDate: e.target.value }))}
                                            />
                                        ) : formatDate(entry.consumptionDate)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ minWidth: 90 }}>
                                        {isEditing ? (
                                            <TextField
                                                data-help="merchandising.consumption.col.producedQty" size="small" type="number" value={editDraft.producedQty}
                                                onChange={e => setEditDraft(prev => ({ ...prev, producedQty: e.target.value }))}
                                            />
                                        ) : (entry.producedQty ?? '—')}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 130 }}>
                                        {isEditing ? (
                                            <TextField
                                                data-help="merchandising.consumption.col.reference" size="small" fullWidth value={editDraft.reference} inputProps={{ maxLength: MAX_REFERENCE }}
                                                onChange={e => setEditDraft(prev => ({ ...prev, reference: e.target.value }))}
                                            />
                                        ) : (entry.reference ?? '—')}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>
                                        {isEditing ? (
                                            <TextField
                                                data-help="merchandising.consumption.col.remarks" size="small" fullWidth value={editDraft.remarks}
                                                onChange={e => setEditDraft(prev => ({ ...prev, remarks: e.target.value }))}
                                            />
                                        ) : (entry.remarks ?? '—')}
                                    </TableCell>
                                    {canManage && (
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {isEditing ? (
                                                <>
                                                    <IconButton data-help="merchandising.consumption.saveEdit" size="small" onClick={() => handleSaveEdit(entry.id)} disabled={saving} aria-label="save">
                                                        <SaveIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton data-help="merchandising.consumption.cancelEdit" size="small" onClick={cancelEdit} disabled={saving} aria-label="cancel edit">
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <>
                                                    <IconButton data-help="merchandising.consumption.edit" size="small" onClick={() => startEdit(entry)} disabled={saving} aria-label="edit">
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton data-help="merchandising.consumption.delete" size="small" onClick={() => handleDelete(entry.id)} disabled={saving} aria-label="delete">
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                        {adding && (
                            <TableRow data-testid="consumption-add-row">
                                <TableCell sx={{ minWidth: 200 }}>
                                    <ItemAutocomplete
                                        data-help="merchandising.consumption.col.item"
                                        value={addDraft.item}
                                        onChange={item => setAddDraft(prev => ({ ...prev, item, stockLotId: null }))}
                                        label="" placeholder="Item"
                                    />
                                </TableCell>
                                <TableCell sx={{ minWidth: 220 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <TextField
                                            data-help="merchandising.consumption.col.lot" select size="small" fullWidth label="Store" value={addDraft.storeId}
                                            onChange={e => setAddDraft(prev => ({ ...prev, storeId: e.target.value, stockLotId: null }))}
                                        >
                                            <MenuItem value="">—</MenuItem>
                                            {stores.map(s => <MenuItem key={s.id} value={s.id}>{s.storeName}</MenuItem>)}
                                        </TextField>
                                        <StockLotPicker
                                            storeId={addDraft.storeId || undefined}
                                            itemId={addDraft.item?.id}
                                            value={addDraft.stockLotId}
                                            onChange={lotId => setAddDraft(prev => ({ ...prev, stockLotId: lotId }))}
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ minWidth: 130 }}>
                                    <TextField
                                        data-help="merchandising.consumption.col.materialType" select size="small" fullWidth value={addDraft.materialType}
                                        onChange={e => setAddDraft(prev => ({ ...prev, materialType: e.target.value as MaterialType }))}
                                    >
                                        <MenuItem value="">—</MenuItem>
                                        {MATERIAL_TYPES.map(t => <MenuItem key={t} value={t}>{enumLabel(t)}</MenuItem>)}
                                    </TextField>
                                </TableCell>
                                <TableCell align="right" sx={{ minWidth: 90 }}>
                                    <TextField
                                        data-help="merchandising.consumption.col.consumedQty" size="small" type="number" value={addDraft.consumedQty}
                                        onChange={e => setAddDraft(prev => ({ ...prev, consumedQty: e.target.value }))}
                                    />
                                </TableCell>
                                <TableCell sx={{ minWidth: 80 }}>
                                    <TextField
                                        data-help="merchandising.consumption.col.uom" size="small" value={addDraft.uom} inputProps={{ maxLength: MAX_UOM }}
                                        onChange={e => setAddDraft(prev => ({ ...prev, uom: e.target.value }))}
                                    />
                                </TableCell>
                                <TableCell sx={{ minWidth: 140 }}>
                                    <TextField
                                        data-help="merchandising.consumption.col.date" size="small" type="date" value={addDraft.consumptionDate}
                                        InputLabelProps={{ shrink: true }}
                                        onChange={e => setAddDraft(prev => ({ ...prev, consumptionDate: e.target.value }))}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ minWidth: 90 }}>
                                    <TextField
                                        data-help="merchandising.consumption.col.producedQty" size="small" type="number" value={addDraft.producedQty}
                                        onChange={e => setAddDraft(prev => ({ ...prev, producedQty: e.target.value }))}
                                    />
                                </TableCell>
                                <TableCell sx={{ minWidth: 130 }}>
                                    <TextField
                                        data-help="merchandising.consumption.col.reference" size="small" fullWidth value={addDraft.reference} inputProps={{ maxLength: MAX_REFERENCE }}
                                        onChange={e => setAddDraft(prev => ({ ...prev, reference: e.target.value }))}
                                    />
                                </TableCell>
                                <TableCell sx={{ minWidth: 150 }}>
                                    <TextField
                                        data-help="merchandising.consumption.col.remarks" size="small" fullWidth value={addDraft.remarks}
                                        onChange={e => setAddDraft(prev => ({ ...prev, remarks: e.target.value }))}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <IconButton data-help="merchandising.consumption.saveNew" {...helpWhy(addWhy ? { kind: 'status', message: addWhy } : null)} size="small" onClick={handleAdd} disabled={saving || !addDraft.item} aria-label="save new entry">
                                        <SaveIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton data-help="merchandising.consumption.cancelNew" size="small" onClick={cancelAdd} disabled={saving} aria-label="cancel new entry">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default MaterialConsumptionEditor;
